import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Checkbox,
    Chip,
    CircularProgress,
    Alert,
    Autocomplete,
} from "@mui/material";
import { UploadFile, CheckCircle, Warning } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useDropzone } from "react-dropzone";
import dayjs from "dayjs";

import api from "../../api/axiosInstance";
import sailingService from "../../services/sailingService";
import containerService from "../../services/containerService";
import PageHeader from "../../components/common/PageHeader";
import { VESSEL_OPTIONS, PORT_OPTIONS, matchVesselOption, matchPortOption } from "../../constants/sailingOptions";

const MATCH_CONFIG = {
    matched: { label: "Matched", color: "success", icon: <CheckCircle sx={{ fontSize: 16 }} /> },
    no_match: { label: "No item found with this D/R", color: "default", icon: <Warning sx={{ fontSize: 16 }} /> },
};

const AdminBolUpload = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams] = useSearchParams();
    // Deep-linked from a sailing that doesn't have a BOL saved yet (Sailing
    // Detail page's "Upload BOL" button) — pre-selects that sailing once
    // the review screen's existingSailings list has loaded.
    const preselectSailingId = searchParams.get("sailingId");

    const [file, setFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [parsed, setParsed] = useState(null); // { sailingInfo, lineItems }
    const [sailingInfo, setSailingInfo] = useState(null);
    const [items, setItems] = useState([]);
    const [existingSailings, setExistingSailings] = useState([]);
    const [selectedExistingSailing, setSelectedExistingSailing] = useState(null);
    const [confirming, setConfirming] = useState(false);
    const [containerOptions, setContainerOptions] = useState([]);
    const [bolDocument, setBolDocument] = useState(null); // { s3Key, originalName, mimeType, size } from parse

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: { "application/pdf": [".pdf"] },
        maxFiles: 1,
        onDrop: (accepted) => {
            if (accepted[0]) setFile(accepted[0]);
        },
    });

    const handleParse = async () => {
        if (!file) return;
        setParsing(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            const { data } = await api.post("/sailing/bol/parse", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            setParsed(data.data);
            setBolDocument(data.data.bolDocument || null);
            // The BOL only ever prints a departure date — arrival isn't on it.
            // Default the estimated arrival to 2 days after whatever departure
            // date got parsed (same Port Everglades -> Cayman transit time
            // every sailing on this route already takes), purely as a
            // starting point the admin can still edit before confirming.
            const parsedDeparture = data.data.sailingInfo.departureDate;
            const defaultArrival = parsedDeparture
                ? dayjs(parsedDeparture).add(2, "day").format("YYYY-MM-DD")
                : "";

            setSailingInfo({
                ...data.data.sailingInfo,
                // Snap the raw BOL text (all-caps, carrier's own abbreviations) to
                // the matching dropdown option so it's pre-selected instead of
                // left as free text the admin has to re-pick — see
                // matchVesselOption/matchPortOption for the known variants.
                vessel: matchVesselOption(data.data.sailingInfo.vessel),
                portOfLoading: matchPortOption(data.data.sailingInfo.portOfLoading),
                portOfDischarge: matchPortOption(data.data.sailingInfo.portOfDischarge),
                departureDate: parsedDeparture || "",
                estimatedArrivalDate: defaultArrival,
            });
            setItems(
                data.data.lineItems.map((item) => ({
                    ...item,
                    // Every matched item is pre-selected — admin unchecks
                    // any that don't actually belong on this BOL.
                    selectedItemIds: new Set((item.matchedItems || []).map((m) => m.itemId)),
                })),
            );

            const { data: sailingsData } = await sailingService.getAll();
            const sailings = sailingsData.data.schedules || [];
            setExistingSailings(sailings);
            if (preselectSailingId) {
                const preselected = sailings.find(
                    (s) => s._id === preselectSailingId || s.sailingNumber === preselectSailingId,
                );
                if (preselected) setSelectedExistingSailing(preselected);
            }

            const { data: containersData } = await containerService.getAll({ limit: 200 });
            setContainerOptions(containersData.data.containers || []);
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to parse BOL", { variant: "error" });
        } finally {
            setParsing(false);
        }
    };

    // Lets the admin fill in/correct any of a line's own fields before
    // confirming — the PDF parse can miss a container/seal, or a D/R the
    // BOL shows just hasn't been typed into the item yet. Applies to
    // whichever item(s) end up selected under this line.
    const updateLine = (idx, patch) => {
        setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    };

    // Picking an existing container from the registry auto-fills its type
    // and seal — the whole point of picking-not-typing is that those two
    // travel with the container number instead of being re-typed (and
    // occasionally mistyped) every time.
    const applyContainerSelection = (idx, container) => {
        if (!container) return;
        if (typeof container === "string") {
            updateLine(idx, { containerNumber: container });
            return;
        }
        updateLine(idx, {
            containerNumber: container.containerNumber,
            containerType: container.containerType || "",
            sealNumber: container.sealNumber || "",
        });
    };

    const toggleMatchedItem = (idx, itemId) => {
        setItems((prev) =>
            prev.map((it, i) => {
                if (i !== idx) return it;
                const next = new Set(it.selectedItemIds);
                if (next.has(itemId)) next.delete(itemId);
                else next.add(itemId);
                return { ...it, selectedItemIds: next };
            }),
        );
    };

    // Only relevant when creating a NEW sailing (assigning to an existing one
    // via selectedExistingSailing is deliberately a different sailing, not a
    // dupe) — catches re-parsing the same BOL twice, or a second BOL that
    // happens to share a voyage number with a sailing already on file.
    const duplicateVoyageSailing =
        !selectedExistingSailing && sailingInfo?.voyage?.trim()
            ? existingSailings.find(
                  (s) => (s.voyageNumber || "").trim().toLowerCase() === sailingInfo.voyage.trim().toLowerCase(),
              )
            : null;

    const canConfirm =
        items.some((i) => i.selectedItemIds.size > 0) &&
        !duplicateVoyageSailing &&
        (selectedExistingSailing ||
            (sailingInfo?.vessel && sailingInfo?.departureDate && sailingInfo?.estimatedArrivalDate));

    const handleConfirm = async () => {
        setConfirming(true);
        try {
            const confirmedItems = items.flatMap((line) =>
                (line.matchedItems || [])
                    .filter((m) => line.selectedItemIds.has(m.itemId))
                    .map((m) => ({
                        shipmentId: m.shipmentId,
                        itemId: m.itemId,
                        drNumber: line.drNumber,
                        containerNumber: line.containerNumber,
                        containerType: line.containerType,
                        sealNumber: line.sealNumber,
                        weightLb: line.weightLb,
                        cbf: line.cbf,
                        packageQty: line.quantity || undefined,
                        packageType: line.unit || undefined,
                    })),
            );

            const { data } = await api.post("/sailing/bol/confirm", {
                sailingInfo,
                existingSailingId: selectedExistingSailing?._id || undefined,
                confirmedItems,
                bolDocument,
            });

            enqueueSnackbar(data.message || "Items recorded successfully", { variant: "success" });
            navigate(`/admin/sailing/${data.data.schedule.sailingNumber}`);
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to confirm assignment", { variant: "error" });
        } finally {
            setConfirming(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Upload Bill of Lading"
                subtitle="Extract sailing and shipment data from a BOL PDF"
            />

            {!parsed ? (
                <Card>
                    <CardContent>
                        <Box
                            {...getRootProps()}
                            sx={{
                                border: "2px dashed",
                                borderColor: isDragActive ? "primary.main" : "divider",
                                borderRadius: 2,
                                p: 5,
                                textAlign: "center",
                                cursor: "pointer",
                                bgcolor: isDragActive ? "rgba(13,98,155,0.04)" : "action.hover",
                            }}
                        >
                            <input {...getInputProps()} />
                            <UploadFile sx={{ fontSize: 44, color: "text.disabled", mb: 1.5 }} />
                            <Typography fontWeight={600} mb={0.5}>
                                {file ? file.name : "Drag & drop a BOL PDF here, or click to browse"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                PDF only, max 15MB
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            fullWidth
                            sx={{ mt: 2.5 }}
                            onClick={handleParse}
                            disabled={!file || parsing}
                        >
                            {parsing ? <CircularProgress size={18} color="inherit" /> : "Parse BOL"}
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {!bolDocument && (
                        <Alert severity="warning">
                            The PDF itself couldn't be saved for later download — parsing still succeeded, so you can
                            continue, but there won't be a "Download BOL" copy on this sailing afterward.
                        </Alert>
                    )}

                    {/* Sailing info review */}
                    <Card>
                        <CardContent>
                            <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
                                Sailing Details {selectedExistingSailing ? "(using existing sailing)" : "(new sailing)"}
                            </Typography>

                            <Box sx={{ mb: 2 }}>
                                <Autocomplete
                                    options={existingSailings}
                                    value={selectedExistingSailing}
                                    onChange={(_, val) => setSelectedExistingSailing(val)}
                                    getOptionLabel={(o) => `${o.sailingNumber} — ${o.vesselName}`}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Assign to existing sailing instead (optional)" />
                                    )}
                                />
                            </Box>

                            {!selectedExistingSailing && duplicateVoyageSailing && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    Another sailing already exists for this voyage number —{" "}
                                    {duplicateVoyageSailing.sailingNumber} ({duplicateVoyageSailing.vesselName}).
                                    Pick it from "Assign to existing sailing instead" above, or correct the voyage
                                    number if this is actually a different sailing.
                                </Alert>
                            )}

                            {!selectedExistingSailing && (
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                        gap: 2,
                                    }}
                                >
                                    <Autocomplete
                                        freeSolo
                                        options={VESSEL_OPTIONS}
                                        value={sailingInfo.vessel || ""}
                                        onInputChange={(_, val) => setSailingInfo((p) => ({ ...p, vessel: val }))}
                                        renderInput={(params) => <TextField {...params} label="Vessel Name *" />}
                                    />
                                    <TextField
                                        label="Voyage Number"
                                        value={sailingInfo.voyage || ""}
                                        onChange={(e) => setSailingInfo((p) => ({ ...p, voyage: e.target.value }))}
                                        error={!!duplicateVoyageSailing}
                                        helperText={
                                            duplicateVoyageSailing
                                                ? `Another sailing already exists for this voyage number (${duplicateVoyageSailing.sailingNumber} — ${duplicateVoyageSailing.vesselName})`
                                                : undefined
                                        }
                                    />
                                    <TextField
                                        label="BOL Number (reference)"
                                        value={sailingInfo.bolNumber || ""}
                                        onChange={(e) => setSailingInfo((p) => ({ ...p, bolNumber: e.target.value }))}
                                    />
                                    <TextField
                                        label="Booking Number"
                                        value={sailingInfo.bookingNumber || ""}
                                        onChange={(e) => setSailingInfo((p) => ({ ...p, bookingNumber: e.target.value }))}
                                    />
                                    <Autocomplete
                                        freeSolo
                                        options={PORT_OPTIONS}
                                        value={sailingInfo.portOfLoading || ""}
                                        onInputChange={(_, val) => setSailingInfo((p) => ({ ...p, portOfLoading: val }))}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Origin Port"
                                                helperText="Not reliably parseable — please verify or pick from the list"
                                            />
                                        )}
                                    />
                                    <Autocomplete
                                        freeSolo
                                        options={PORT_OPTIONS}
                                        value={sailingInfo.portOfDischarge || ""}
                                        onInputChange={(_, val) => setSailingInfo((p) => ({ ...p, portOfDischarge: val }))}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Destination Port"
                                                helperText="Not reliably parseable — please verify or pick from the list"
                                            />
                                        )}
                                    />
                                    <TextField
                                        label="Departure Date *"
                                        type="date"
                                        value={sailingInfo.departureDate || ""}
                                        onChange={(e) => setSailingInfo((p) => ({ ...p, departureDate: e.target.value }))}
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        sx={{
                                            "& .MuiInputLabel-root": {
                                                backgroundColor: "background.paper",
                                                px: 0.5,
                                                transform: "translate(14px, -9px) scale(0.75)",
                                            },
                                        }}
                                    />
                                    <TextField
                                        label="Estimated Arrival Date"
                                        type="date"
                                        required
                                        value={sailingInfo.estimatedArrivalDate || ""}
                                        onChange={(e) => setSailingInfo((p) => ({ ...p, estimatedArrivalDate: e.target.value }))}
                                        helperText="Not on the BOL — defaulted to 2 days after departure, please verify"
                                        fullWidth
                                        slotProps={{ inputLabel: { shrink: true } }}
                                        sx={{
                                            "& .MuiInputLabel-root": {
                                                backgroundColor: "background.paper",
                                                px: 0.5,
                                                transform: "translate(14px, -9px) scale(0.75)",
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Freight charges review — extracted from the Master BOL's
                        charges section, editable before confirm since the PDF
                        text parse can misfire on unusual layouts. */}
                    <Card>
                        <CardContent>
                            <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
                                Freight Charges (from BOL)
                            </Typography>

                            {sailingInfo.charges?.length > 0 ? (
                                <Box sx={{ mb: 2 }}>
                                    <Table size="small">
                                        <TableBody>
                                            {sailingInfo.charges.map((c, i) => (
                                                <TableRow key={i}>
                                                    <TableCell sx={{ border: 0, py: 0.5 }}>{c.label}</TableCell>
                                                    <TableCell align="right" sx={{ border: 0, py: 0.5, fontFamily: "monospace" }}>
                                                        ${c.amount?.toFixed(2)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Box>
                            ) : (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    No itemized charges were found on this BOL — the totals below can still be entered manually.
                                </Alert>
                            )}

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                    gap: 2,
                                }}
                            >
                                <TextField
                                    label="Total Freight & Charges (US$)"
                                    type="number"
                                    value={sailingInfo.totalFreightAndChargesUsd ?? ""}
                                    onChange={(e) =>
                                        setSailingInfo((p) => ({ ...p, totalFreightAndChargesUsd: e.target.value === "" ? null : Number(e.target.value) }))
                                    }
                                />
                                <TextField
                                    label="Total Freight & Charges (CI$)"
                                    type="number"
                                    value={sailingInfo.totalFreightAndChargesCi ?? ""}
                                    onChange={(e) =>
                                        setSailingInfo((p) => ({ ...p, totalFreightAndChargesCi: e.target.value === "" ? null : Number(e.target.value) }))
                                    }
                                />
                                <TextField
                                    label="Cayman Crane Surcharge"
                                    type="number"
                                    value={sailingInfo.craneSurchargeAmount ?? ""}
                                    onChange={(e) =>
                                        setSailingInfo((p) => ({ ...p, craneSurchargeAmount: e.target.value === "" ? 0 : Number(e.target.value) }))
                                    }
                                />
                                <TextField
                                    label="Freight Excluding Crane Surcharge"
                                    type="number"
                                    value={sailingInfo.freightExcludingCraneSurcharge ?? ""}
                                    onChange={(e) =>
                                        setSailingInfo((p) => ({ ...p, freightExcludingCraneSurcharge: e.target.value === "" ? null : Number(e.target.value) }))
                                    }
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Line items review */}
                    <Card>
                        <CardContent>
                            <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
                                Extracted Dock Receipts ({items.reduce((sum, i) => sum + i.selectedItemIds.size, 0)} item(s) selected)
                            </Typography>

                            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                Each row is one D/R from the BOL, matched against items that already carry that same
                                dock receipt number (assigned via the Dock Receipts page). A D/R may match more than
                                one item — uncheck any that don't belong on this BOL. Confirming only updates D/R #,
                                Weight, CBF, Pkg Qty/Type, Container #, Container Type, and Seal # on the item(s)
                                checked, then assigns them to this sailing — nothing else about the item (its
                                description included) is changed. Pick the Container # from the list to auto-fill
                                its Type/Seal and avoid logging a duplicate — type a new number only if it's
                                genuinely not in the registry yet. Rows with no match mean no item has been tagged
                                with that D/R yet.
                            </Alert>

                            <Box sx={{ overflowX: "auto" }}>
                                <Table size="small" sx={{ minWidth: 900 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>D/R #</TableCell>
                                            <TableCell>Description</TableCell>
                                            <TableCell align="right">Weight</TableCell>
                                            <TableCell align="right">CBF</TableCell>
                                            <TableCell align="right">Pkg Qty</TableCell>
                                            <TableCell>Pkg Type</TableCell>
                                            <TableCell>Container #</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Seal #</TableCell>
                                            <TableCell>Match Status</TableCell>
                                            <TableCell>Matched Item(s)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.map((item, idx) => {
                                            const cfg = MATCH_CONFIG[item.matchStatus];
                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell sx={{ minWidth: 110 }}>
                                                        <TextField
                                                            size="small"
                                                            variant="standard"
                                                            value={item.drNumber || ""}
                                                            onChange={(e) => updateLine(idx, { drNumber: e.target.value })}
                                                            sx={{ "& input": { fontFamily: "monospace", fontSize: 12 } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 180 }}>
                                                        <Typography variant="body2" noWrap maxWidth={200} color="text.secondary">
                                                            {item.description || "—"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ minWidth: 90 }}>
                                                        <TextField
                                                            size="small"
                                                            variant="standard"
                                                            type="number"
                                                            value={item.weightLb ?? ""}
                                                            onChange={(e) =>
                                                                updateLine(idx, {
                                                                    weightLb: e.target.value === "" ? null : Number(e.target.value),
                                                                })
                                                            }
                                                            slotProps={{ htmlInput: { min: 0, step: 0.1, style: { textAlign: "right" } } }}
                                                            sx={{ width: 80 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ minWidth: 90 }}>
                                                        <TextField
                                                            size="small"
                                                            variant="standard"
                                                            type="number"
                                                            value={item.cbf ?? ""}
                                                            onChange={(e) =>
                                                                updateLine(idx, {
                                                                    cbf: e.target.value === "" ? null : Number(e.target.value),
                                                                })
                                                            }
                                                            slotProps={{ htmlInput: { min: 0, step: 0.01, style: { textAlign: "right" } } }}
                                                            sx={{ width: 80 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ minWidth: 80 }}>
                                                        <TextField
                                                            size="small"
                                                            variant="standard"
                                                            type="number"
                                                            value={item.quantity ?? ""}
                                                            onChange={(e) =>
                                                                updateLine(idx, {
                                                                    quantity: e.target.value === "" ? null : Number(e.target.value),
                                                                })
                                                            }
                                                            slotProps={{ htmlInput: { min: 1, style: { textAlign: "right" } } }}
                                                            sx={{ width: 70 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 80 }}>
                                                        <TextField
                                                            size="small"
                                                            variant="standard"
                                                            placeholder="BX/PC/CR"
                                                            value={item.unit || ""}
                                                            onChange={(e) => updateLine(idx, { unit: e.target.value.toUpperCase() })}
                                                            sx={{ "& input": { fontFamily: "monospace", fontSize: 11 } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 170 }}>
                                                        <Autocomplete
                                                            size="small"
                                                            freeSolo
                                                            options={containerOptions}
                                                            getOptionLabel={(o) => (typeof o === "string" ? o : o.containerNumber)}
                                                            isOptionEqualToValue={(o, v) =>
                                                                (typeof o === "string" ? o : o.containerNumber) ===
                                                                (typeof v === "string" ? v : v?.containerNumber)
                                                            }
                                                            inputValue={item.containerNumber || ""}
                                                            onInputChange={(_, val, reason) => {
                                                                if (reason === "input") updateLine(idx, { containerNumber: val });
                                                            }}
                                                            onChange={(_, val) => applyContainerSelection(idx, val)}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    variant="standard"
                                                                    placeholder="Pick or type new…"
                                                                    sx={{ "& input": { fontFamily: "monospace", fontSize: 11 } }}
                                                                />
                                                            )}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 90 }}>
                                                        <TextField
                                                            size="small"
                                                            variant="standard"
                                                            value={item.containerType || ""}
                                                            onChange={(e) => updateLine(idx, { containerType: e.target.value })}
                                                            sx={{ "& input": { fontFamily: "monospace", fontSize: 11 } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 100 }}>
                                                        <TextField
                                                            size="small"
                                                            variant="standard"
                                                            value={item.sealNumber || ""}
                                                            onChange={(e) => updateLine(idx, { sealNumber: e.target.value })}
                                                            sx={{ "& input": { fontFamily: "monospace", fontSize: 11 } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={cfg.label}
                                                            size="small"
                                                            color={cfg.color}
                                                            icon={cfg.icon}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 260 }}>
                                                        {(item.matchedItems || []).length === 0 ? (
                                                            <Typography variant="caption" color="text.secondary">
                                                                Assign this D/R to an item first via the Dock Receipts page, then re-upload.
                                                            </Typography>
                                                        ) : (
                                                            (item.matchedItems || []).map((m) => (
                                                                <Box key={m.itemId} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={item.selectedItemIds.has(m.itemId)}
                                                                        onChange={() => toggleMatchedItem(idx, m.itemId)}
                                                                        sx={{ p: 0.5, mt: 0.25 }}
                                                                    />
                                                                    <Typography variant="body2">
                                                                        <b>{m.shipmentTrackingNumber}</b> — {m.customer?.firstName} {m.customer?.lastName}
                                                                        {m.trackingNumber && (
                                                                            <Typography component="span" variant="caption" color="text.secondary">
                                                                                {" "}
                                                                                ({m.trackingNumber})
                                                                            </Typography>
                                                                        )}
                                                                        {m.alreadyOnSailing && (
                                                                            <Chip label="Already on a sailing" size="small" color="warning" sx={{ ml: 1 }} />
                                                                        )}
                                                                    </Typography>
                                                                </Box>
                                                            ))
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Box>
                        </CardContent>
                    </Card>

                    <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
                        <Button variant="outlined" onClick={() => { setParsed(null); setFile(null); }}>
                            Start Over
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirm}
                            disabled={!canConfirm || confirming}
                        >
                            {confirming ? <CircularProgress size={18} color="inherit" /> : "Confirm & Assign"}
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default AdminBolUpload;